import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DSSetup, RecordData, Service } from 'webapp-types';
import {
	BlockVisualization,
	BlockVisualizationSpecial,
	DataSelectType,
	GranulationTypeId,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { copyObject } from 'webapp/utils/common';

import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import BarLoaderComponent from 'webapp/components/bar-loader';
import BlockSingleComponent from 'webapp/components/block/single';
import MetricDetailsChartEmptyComponent from 'webapp/components/metric-details/chart-empty';
import MetricInfoIconComponent from 'webapp/components/metric-info/icon';

import type { Goal } from 'webapp/models-custom';
import type Block from 'webapp/models-custom/block';
import type BlockState from 'webapp/models-custom/block-state';
import BoardState from 'webapp/models-custom/board-state';

import type { RSjqXHR } from 'webapp/services/request';

interface Args {
	chartState: GoalInsightsChartState;
	forceLoadingState: boolean;
	origin: string;
	model: Goal;
}

export class GoalInsightsChartState {
	@tracked isVisualizationModuleDataLoading = false;
	@tracked isDataEmpty = true;
	@tracked isHorizonPollingExhausted = false;
	@tracked block: Block | null = null;

	// The block this chart is currently showing. See MetricInsightsChartState.
	activeBlockUUID = '';
}

export default class GoalInsightsDataVisualizationIndexComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('store') declare storeService: Service.Store;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	@tracked boardState: BoardState | null = null;
	@tracked _isAnomalySwitchChecked = false;

	private visualizationModuleDataXHR: RSjqXHR | null = null;
	private blockUUID = this.generateUUID() as string;
	private paramHash = '';
	private visualization = BlockVisualization.LINE_CHART;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.recreateVisualizationModule();

		this.eventManagerService.on(AppEvent.GOAL_INSIGHTS_GOAL_EDITED, this, () => {
			this.recreateVisualizationModule(true);
		});
	}

	get model() {
		return this.args.model;
	}

	get block() {
		return this.boardState?.selectedBlock;
	}

	get metricSetup() {
		return this.model.mainMetricSetup;
	}

	get granulationTypeId() {
		return this.model.dateRange?.granulationTypeId;
	}

	get isChartCumulative() {
		return this.model.isReachCondition;
	}

	get isEmpty() {
		const block = this.block;

		return !!block && block.isDataEmpty && !block.isSyncing;
	}

	get blockState(): BlockState | null {
		const block = this.block;
		if (!block) {
			return null;
		}
		return this.boardState?.getBlockState(block) || null;
	}

	get isLoading() {
		return (
			this.args.chartState.isVisualizationModuleDataLoading || !!this.args.forceLoadingState
		);
	}

	private generateUUID(string = true) {
		const numericId = this.mmGoalsService.generateGoalInsightsUUID();
		return string ? numericId.toString() : numericId;
	}

	private unloadOldBlockData(): void {
		const block = this.block;
		if (block) {
			this.garbageCollectorService.clearConnectedRecords('block', block);
			block.model.unloadRecord();
			this.args.chartState.block = null;
		}
	}

	private createNewBlock() {
		const dateRange: DateRangeInterface.FormulaInterface | null = this.model.dateRange
			? copyObject(this.model.dateRange?.formula)
			: null;

		this.blockUUID = this.generateUUID() as string;

		const params = {
			id: this.blockUUID,
			order: 0,
			position: {
				x: 0,
				y: 0,
				w: 16,
				h: 8,
				m: true,
			},
			visualization: this.visualization,
			isSpline: true,
			name: '',
		};

		const block = this.mmBlocksService.createNewModel(params);
		this.boardState = BoardState.createForSingleBlock(block);
		next(() => {
			this.args.chartState.block = block;
		});

		const blockSetup = block.blockSetup;
		blockSetup.isAutosaveOn = false;

		const visualization = blockSetup.visualization;
		visualization.forceDChartRendererType = BlockVisualizationSpecial.GOAL_CHART;
		visualization.checkToUpdateDataCache();

		const dateRangeCopy: DateRangeInterface.FormulaInterface | null = copyObject(dateRange),
			granulation = this.block?.blockSetup.limitations.dateRange?.formula.granulation;

		let dateRangeWithGranulation: DateRangeInterface.FormulaInterface | undefined;
		if (dateRangeCopy?.range && granulation) {
			dateRangeWithGranulation = {
				range: dateRangeCopy.range,
				granulation: {
					...granulation,
					cumulative: this.isChartCumulative,
				},
			};
		}

		const metricLimitations = this.block?.blockSetup.limitations.createCopy();
		if (metricLimitations?.dateRange && dateRangeWithGranulation) {
			metricLimitations.updateDateRangeFromFormula(dateRangeWithGranulation);
		}

		const firstDsId = this.generateUUID(),
			metricSetupData = this.metricSetup.metricSetupWithoutFiltersData!;

		/* firstDs is what blockSetup.firstShownDataSelect would return in next runloop */
		const firstDs = blockSetup.createPrimaryMainDataSelect({
			id: firstDsId,
			blockId: block.id,
			dateRange: metricLimitations?.dateRange?.formula,
			metricSetupKey: metricSetupData.key,
			metricSetupSpaceAccessId: metricSetupData.spaceAccessId,
			metricSetupDimensions: metricSetupData.dimensions,
			metricSetupFilters: this.metricSetup.filtersData,
		});

		// add goal compare and goal dataSelect to firstDs
		const goalDs = blockSetup.addGoalCompareAndDataSelect(
			firstDs,
			this.model.goalKeyLong || '',
			metricSetupData.spaceAccessId,
			{
				id: this.generateUUID(),
				updateFrom: firstDsId,
				dateRange: metricLimitations?.dateRange?.formula,
			},
		);

		const firstDsCompares = firstDs.limitations.compareCMs;
		firstDsCompares.forEach((compare) => {
			compare.id = this.generateUUID(false) as number;
		});
		firstDs.updateComparesFormulaArrayFromCompareModels(firstDsCompares);

		// additional lines
		const fakeSavedDataSelects: RecordData.DataSelect.Serialized[] = [];

		const compareDs = blockSetup.addSecondaryDataSelectForDataSelect(
			firstDs,
			DataSelectType.COMPARE,
			{
				id: this.generateUUID(),
				updateFrom: firstDsId,
			},
		);

		const totalDateRangeCopy = firstDs.limitations.dateRange?.createCopy();

		if (!totalDateRangeCopy) {
			return;
		}

		totalDateRangeCopy.updateFromPredefinedGranulationId(GranulationTypeId.TOTAL);

		// copying compares from the main ds but swap ids
		const compares = copyObject(firstDs.compares);
		compares.forEach((c: DSSetup.Compare) => {
			c.id = this.generateUUID(false) as number;
		});

		const numberDs = blockSetup.addSecondaryDataSelectForDataSelect(
			firstDs,
			DataSelectType.MAIN,
			{
				id: this.generateUUID(),
				updateFrom: firstDsId,
				compares,
				dateRange: totalDateRangeCopy.formula,
			},
		);

		const fakeFirstDs = firstDs.toSerializedJSON(true);
		fakeSavedDataSelects.push(fakeFirstDs);
		firstDs.model.deleteRecord();

		if (numberDs) {
			const fakeNumberDs = numberDs.toSerializedJSON(true);
			fakeSavedDataSelects.push(fakeNumberDs);
			numberDs.model.deleteRecord();
		}

		if (goalDs) {
			const fakeGoalDs = goalDs.toSerializedJSON(true);
			fakeSavedDataSelects.push(fakeGoalDs);
			goalDs.model.deleteRecord();
		}

		if (compareDs) {
			const fakeCompareDs = compareDs.toSerializedJSON(true);
			fakeSavedDataSelects.push(fakeCompareDs);
			compareDs.model.deleteRecord();
		}

		this.storeService.pushPayload({
			dataSelects: fakeSavedDataSelects,
		});

		// Keep the handle — without it the in-flight abort here and in willDestroy
		// are no-ops, so a superseded request can still write into chartState.
		this.visualizationModuleDataXHR = this.metricDetailsService.requestVisualizationModuleData(
			this.block!,
			this.args.chartState,
			this.blockUUID,
			this.visualizationModuleDataXHR,
		);
	}

	private recreateVisualizationModule(forceNewRequest = false) {
		const dateRange = this.model.dateRange,
			dateRangeId = dateRange?.formulaId;

		const metricSetup = this.metricSetup;
		if (metricSetup.key && !this.args.forceLoadingState) {
			const newParamHash = this.metricDetailsService.getInsightsParamsHash(
				metricSetup,
				this.visualization,
				dateRangeId,
			);

			if (forceNewRequest || this.paramHash !== newParamHash) {
				this.paramHash = newParamHash;

				this.unloadOldBlockData();

				if (this.metricSetup.metric) {
					const chartState = this.args.chartState;

					if (chartState) {
						// Don't wait until requestMe to set to true many next runloops in the future. Set immediately.
						chartState.isDataEmpty = true;
						chartState.isVisualizationModuleDataLoading = true;
					}

					next(() => {
						this.createNewBlock();
					});
				}
			}
		} else {
			this.paramHash = '';
			this.unloadOldBlockData();
		}
	}

	@action
	refreshVisualizationModule() {
		once(this, this.recreateVisualizationModule);
	}

	@action
	teardownView(): void {
		// Use dom event, not willDestroy cause user might quickly navigate through visited routes
		this.eventManagerService.off(AppEvent.GOAL_INSIGHTS_GOAL_EDITED, this);
	}

	override willDestroy() {
		super.willDestroy();

		this.visualizationModuleDataXHR?.abort();

		this.garbageCollectorService._unloadBlockData(this.blockUUID);

		const block = this.block;
		if (block) {
			block.model.unloadRecord();
		}
	}
	<template>
		<div
			class="goal-insights-data-visualization-module"
			{{didUpdate
				this.refreshVisualizationModule
				this.metricSetup.key
				this.metricSetup.spaceAccessId
				this.metricSetup.dimensions
				this.metricSetup.filtersData
				this.model.dateRange.formulaId
				@forceLoadingState
			}}
			{{willDestroy this.teardownView}}
		>

			<div class="goal-insights-metric-info">
				<AvatarOrInitialsComponent @avatar={{this.metricSetup.source.realAvatar}} />
				<div class="text">
					<strong>{{this.metricSetup.selectedMetricTitle}}</strong>
					<span>{{this.metricSetup.source.title}}</span>
				</div>
				{{#if this.metricSetup.metric}}
					<MetricInfoIconComponent @model={{this.metricSetup.metric}} />
				{{/if}}
			</div>

			<div class="goal-insights-board-wrapper">
				{{#if this.isLoading}}
					<img class="empty-image" src="/images/metric-insights/empty.svg" alt />
					<div class="loader">
						<BarLoaderComponent @size="big" />
					</div>
				{{else if this.isEmpty}}
					<MetricDetailsChartEmptyComponent
						@title={{if
							@chartState.isHorizonPollingExhausted
							"Data is still being prepared"
						}}
						@subtitle={{if
							@chartState.isHorizonPollingExhausted
							"This process can take a few minutes. Please try again shortly."
						}}
					/>
				{{else if this.boardState}}
					<BlockSingleComponent
						@boardState={{this.boardState}}
						@skipBlockFetchLoader={{true}}
					/>
				{{/if}}
			</div>

		</div>
	</template>
}
